import { MapPin, ClipboardList, Grid3X3, Users, Wallet, Clock, CheckSquare } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  useClientEventDetail,
  useClientEventGuests,
  useClientEventQuestionnaires,
  useClientEventTimeline,
  useClientExpenseSummary,
  useClientTasks,
} from '../../hooks/usePortal';
import { useLocalDay } from '../../hooks/useDashboard';
import { useEventGuestSummary } from '../../hooks/useEventGuests';
import { GuestProgressCard } from './GuestProgressCard';
import { formatTimeDisplay, isChecklistItemDone, taskDueBucket } from '@peles/shared';

function money(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function Tile({
  icon,
  label,
  value,
  bar,
  footer,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  bar?: number;
  footer?: ReactNode;
}) {
  return (
    <span className="h-full bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-2.5">
      <span className="flex items-center gap-2 text-sm text-gray-500">
        <span className="text-gray-400">{icon}</span>
        {label}
      </span>
      <span className="block text-2xl font-bold text-gray-900">{value}</span>
      {bar != null && (
        <span className="block h-1.5 rounded-full bg-gray-100 overflow-hidden" aria-hidden>
          <span className="block h-full rounded-full bg-primary-600" style={{ width: `${bar}%` }} />
        </span>
      )}
      {footer && <span className="block text-xs text-gray-500">{footer}</span>}
    </span>
  );
}

interface ClientEventOverviewProps {
  eventId: string;
  /** Switch the page to another tab (the attention strip's buttons). */
  onOpenTab: (
    tab:
      | 'questionnaires'
      | 'floor-plan'
      | 'guests'
      | 'expenses'
      | 'timeline'
      | 'tasks'
      | 'invites'
      | 'seating',
  ) => void;
}

/**
 * The client portal's Overview: what is waiting on the client, their guests
 * and payments at a glance, the day's schedule, then the event's details.
 */
export function ClientEventOverview({ eventId, onOpenTab }: ClientEventOverviewProps) {
  const { date: todayKey } = useLocalDay();
  // The tasks assigned to the client: their open ones count as waiting on them.
  const { data: tasks = [] } = useClientTasks(eventId);
  const { data: event, isLoading } = useClientEventDetail(eventId);
  const { data: questionnaires = [] } = useClientEventQuestionnaires(eventId);
  const { data: guests = [] } = useClientEventGuests(eventId);
  const { data: timelines = [] } = useClientEventTimeline(eventId);
  const { data: guestSummary } = useEventGuestSummary(eventId);
  const showMoney = event?.budgetVisibility === 'full';
  const { data: summary } = useClientExpenseSummary(showMoney ? eventId : '');

  if (isLoading) {
    return (
      <div
        className="grid gap-4 md:grid-cols-3 animate-pulse"
        data-testid="client-overview-skeleton"
      >
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-32 rounded-2xl bg-gray-100" />
        ))}
      </div>
    );
  }
  if (!event) return null;

  const waiting = questionnaires.filter((q) => q.status === 'SENT' || q.status === 'IN_PROGRESS');
  const openTasks = tasks.filter((t) => !isChecklistItemDone(t));
  const overdueTasks = openTasks.filter(
    (t) => taskDueBucket(t.dueDate, todayKey) === 'overdue',
  ).length;
  const attending = guests.reduce((s, g) => s + g.attendingTotal, 0);
  const answered = guests.filter((g) => g.rsvpResponse !== 'PENDING').length;
  const paidShare =
    summary && summary.totalPaid + summary.balance > 0
      ? Math.round((summary.totalPaid / (summary.totalPaid + summary.balance)) * 100)
      : undefined;
  const firstEntries = (timelines[0]?.sections ?? [])
    .flatMap((s) => s.entries)
    .filter((e) => e.time)
    .slice(0, 5);

  const startDate = new Date(event.startAt).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const endDate = event.endAt
    ? new Date(event.endAt).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="space-y-6">
      {waiting.length > 0 && (
        <section
          aria-label="Needs your attention"
          className="theme-light rounded-2xl bg-gray-900 text-white px-5 py-4 flex flex-wrap items-center justify-between gap-4"
        >
          <div>
            <p className="text-xs text-gray-300">Needs your attention</p>
            <p className="text-lg font-bold">
              {waiting.length === 1
                ? `"${waiting[0].title}" is waiting for you`
                : `${waiting.length} questionnaires are waiting for you`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab('questionnaires')}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100"
          >
            <ClipboardList size={16} />
            {waiting.length === 1 ? 'Fill it in' : 'Open questionnaires'}
          </button>
        </section>
      )}

      {openTasks.length > 0 && (
        <section
          aria-label="Your tasks"
          className="rounded-2xl border border-gray-200 bg-white px-5 py-4 flex flex-wrap items-center justify-between gap-4"
        >
          <div>
            <p className="text-xs text-gray-500">Your tasks</p>
            <p className="text-lg font-bold text-gray-900">
              {openTasks.length === 1 ? '1 task to do' : `${openTasks.length} tasks to do`}
              {overdueTasks > 0 && <span className="text-red-700"> · {overdueTasks} overdue</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenTab('tasks')}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
          >
            <CheckSquare size={16} />
            Open your tasks
          </button>
        </section>
      )}

      {guestSummary && (
        <GuestProgressCard
          summary={guestSummary}
          onOpenTab={onOpenTab}
          canInvite={event.permissions?.canManageInvites ?? false}
          canSeat={event.permissions?.canManageSeating ?? false}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <button
          type="button"
          onClick={() => onOpenTab('guests')}
          className="text-left card-lift rounded-2xl"
        >
          <Tile
            icon={<Users size={16} />}
            label="Your guests"
            value={
              <>
                {attending} <span className="text-base font-medium text-gray-400">coming</span>
              </>
            }
            bar={guests.length > 0 ? Math.round((answered / guests.length) * 100) : undefined}
            footer={
              guests.length === 0
                ? 'No guests on the list yet'
                : `${answered} of ${guests.length} invitations answered`
            }
          />
        </button>
        {showMoney && summary && (
          <button
            type="button"
            onClick={() => onOpenTab('expenses')}
            className="text-left card-lift rounded-2xl"
          >
            <Tile
              icon={<Wallet size={16} />}
              label="You've paid"
              value={money(summary.totalPaid)}
              bar={paidShare}
              footer={
                summary.balance > 0.005 ? (
                  <span className="text-red-700 font-semibold">
                    {money(summary.balance)} still due
                  </span>
                ) : (
                  'Nothing due right now'
                )
              }
            />
          </button>
        )}
        <button
          type="button"
          onClick={() => onOpenTab('floor-plan')}
          className="text-left card-lift rounded-2xl"
        >
          <Tile
            icon={<Grid3X3 size={16} />}
            label="Floor plan"
            value={<span className="text-lg">See the room</span>}
            footer="Tables, seating and layout"
          />
        </button>
      </div>

      {firstEntries.length > 0 && (
        <section className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Clock size={16} className="text-gray-400" />
              The day, at a glance
            </h2>
            <button
              type="button"
              onClick={() => onOpenTab('timeline')}
              className="text-xs text-primary-700 hover:underline"
            >
              Full timeline
            </button>
          </div>
          <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {firstEntries.map((entry) => (
              <li key={entry.id} className="border-l-[3px] border-primary-500 pl-3 py-1">
                <p className="text-xs text-gray-500">{formatTimeDisplay(entry.time!)}</p>
                <p className="text-sm font-semibold text-gray-900">{entry.title}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Event details</h2>
          {event.eventTypeName && (
            <div>
              <p className="text-xs text-gray-500">Type</p>
              <p className="font-medium">{event.eventTypeName}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">Date</p>
            <p className="font-medium">{startDate}</p>
            {endDate && <p className="text-sm text-gray-600">through {endDate}</p>}
          </div>
          {event.guestCount != null && (
            <div>
              <p className="text-xs text-gray-500">Expected guests</p>
              <p className="font-medium">{event.guestCount}</p>
            </div>
          )}
        </section>

        {(event.venueName || event.venueAddress) && (
          <section className="bg-white rounded-2xl border border-gray-200 p-5 space-y-2">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <MapPin size={16} className="text-gray-400" />
              Venue
            </h2>
            {event.venueName && <p className="font-medium">{event.venueName}</p>}
            {event.venueAddress && <p className="text-sm text-gray-600">{event.venueAddress}</p>}
            {event.venueCity && <p className="text-sm text-gray-600">{event.venueCity}</p>}
          </section>
        )}

        {event.notes && (
          <section className="bg-white rounded-2xl border border-gray-200 p-5 md:col-span-2">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Notes</h2>
            <p className="text-gray-700 whitespace-pre-wrap">{event.notes}</p>
          </section>
        )}
      </div>
    </div>
  );
}
